<template>
  <vs-popup
    class="staff-mobile-security-popup"
    :title="popupTitle"
    @close="onClose"
    :active.sync="open"
    :buttons-hidden="true"
  >
    <div id="staff-mobile-security-content" v-if="selectedStaff" class="p-2">
      <!-- Business Card & PIN Status Container -->
      <div class="flex flex-col items-center">
        <!-- Business Card Preview -->
        <div id="staff-mobile-app-business-card" ref="businessCard" class="business-card-preview mb-4">
          <div class="card-top-stripe"></div>
          <div class="card-header">
            <img :src="companyLogo" alt="Company Logo" class="card-logo" />
            <span class="card-corp-badge">A BRAVER FLAVOUR</span>
          </div>

          <div class="card-body">
            <div class="card-user-info">
              <div class="card-user-profile-wrap">
                <img
                  v-if="userAvatar"
                  :src="userAvatar"
                  alt="User Photo"
                  class="card-user-avatar"
                />
                <div v-else class="card-user-avatar-placeholder">
                  {{ userInitials }}
                </div>
                <div class="card-user-text">
                  <div class="card-user-name">{{ userName }}</div>
                  <div v-if="userRole" class="card-user-role">{{ userRole }}</div>
                </div>
              </div>
              <div class="card-user-email">
                <svg class="email-icon" viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
                <span>{{ userEmail || 'N/A' }}</span>
              </div>
            </div>

            <!-- QR & Mobile Access Column -->
            <div class="card-qr-column">
              <div class="card-qr-box">
                <qrcode-vue
                  :value="qrData"
                  :size="80"
                  :margin="0"
                  render-as="svg"
                  style="display:block; background:#ffffff;"
                  level="M"
                />
              </div>
              <span class="card-qr-label">MOBILE APP ACCESS</span>
            </div>
          </div>

          <div class="card-footer">
            <span class="card-footer-text">INTERNATIONAL TASTE SOLUTIONS</span>
            <div class="brand-dots">
              <span class="brand-dot-pink"></span>
              <span class="brand-dot-yellow"></span>
              <span class="brand-dot-teal"></span>
            </div>
          </div>
        </div>

        <!-- Mobile App PIN Status (Bottom of Card) -->
        <div
          class="w-full p-4 rounded border border-solid d-theme-border-grey-light mb-4"
          style="max-width: 360px;"
          :class="hasPin ? 'bg-light-success' : 'bg-light-warning'"
        >
          <div class="flex items-start">
            <feather-icon
              :icon="hasPin ? 'CheckCircleIcon' : 'AlertCircleIcon'"
              :svgClasses="'w-5 h-5 mr-3 mt-1 flex-shrink-0 ' + (hasPin ? 'text-success' : 'text-warning')"
            />
            <div class="flex-grow">
              <div class="font-semibold text-sm mb-1" :class="hasPin ? 'text-success' : 'text-warning'">
                {{ hasPin ? 'Mobile App PIN Set' : 'Mobile App PIN Not Configured' }}
              </div>
              <p class="text-xs text-muted mb-0" v-if="hasPin && pinUpdatedAt">
                Last updated: <strong>{{ pinUpdatedAt }}</strong>
              </p>
              <p class="text-xs text-muted mb-0" v-else>
                This staff member has not set their 6-digit Mobile App PIN yet. They can set it in their user profile under Mobile App Security.
              </p>
            </div>
          </div>
        </div>

        <!-- Print Card Action Button -->
        <div class="w-full flex justify-center" style="max-width: 360px;">
          <vs-button
            icon-pack="feather"
            icon="icon-printer"
            color="primary"
            class="w-full justify-center"
            style="height: 42px; font-weight: 600;"
            @click="onClickPrintCard"
          >
            Print Card
          </vs-button>
        </div>
      </div>
    </div>
  </vs-popup>
</template>

<script>
import QrcodeVue from 'qrcode.vue'
import { Printd } from 'printd'
import moment from 'moment'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import ErrorMessages from '@/mixins/ErrorMessages'

const StaffRepository = RepositoryFactory.get('staff')

export default {
  name: 'StaffMobileSecurityPopUp',
  components: {
    QrcodeVue
  },
  mixins: [ErrorMessages],
  data() {
    return {
      open: false,
      selectedStaff: null,
      companyLogo: require('@/assets/images/logo/its_logo_horizontal.png')
    }
  },
  computed: {
    userName() {
      return (this.selectedStaff && this.selectedStaff.name) || 'User'
    },
    userEmail() {
      return (this.selectedStaff && this.selectedStaff.email) || ''
    },
    userRole() {
      if (!this.selectedStaff) return ''
      if (this.selectedStaff.role && this.selectedStaff.role.name) {
        return this.selectedStaff.role.name
      }
      if (typeof this.selectedStaff.role === 'string') {
        return this.selectedStaff.role
      }
      return this.selectedStaff.job_title || ''
    },
    userAvatar() {
      return (this.selectedStaff && this.selectedStaff.avatar) || ''
    },
    userInitials() {
      if (!this.userName) return 'U'
      const parts = this.userName.trim().split(/\s+/)
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase()
      }
      return parts[0].substring(0, 2).toUpperCase()
    },
    qrData() {
      return JSON.stringify({ email: this.userEmail })
    },
    hasPin() {
      return !!(this.selectedStaff && this.selectedStaff.mobile_app_pin_updated_at)
    },
    pinUpdatedAt() {
      if (!this.selectedStaff || !this.selectedStaff.mobile_app_pin_updated_at) return ''
      return this.formatDateTime(this.selectedStaff.mobile_app_pin_updated_at)
    },
    popupTitle() {
      if (this.userName && this.userName !== 'User') {
        return `MOBILE APP SECURITY - ${this.userName.toUpperCase()}`
      }
      return 'STAFF MOBILE APP SECURITY'
    }
  },
  methods: {
    async openPopup(staffOrId) {
      if (typeof staffOrId === 'object' && staffOrId !== null) {
        this.selectedStaff = staffOrId
        this.open = true
      } else if (staffOrId) {
        this.open = true
        this.$vs.loading({
          container: '#staff-mobile-security-content',
          scale: 0.45
        })
        try {
          const { data } = await StaffRepository.getStaff(staffOrId)
          if (data && data.data) {
            this.selectedStaff = data.data
          }
        } catch (e) {
          this.convertAndNotifyError(e)
        } finally {
          this.$vs.loading.close('#staff-mobile-security-content > .con-vs-loading')
        }
      }
    },
    onClose() {
      this.open = false
    },
    formatDateTime(dateStr) {
      if (!dateStr) return ''
      return moment(dateStr).format('YYYY-MM-DD HH:mm:ss')
    },
    onClickPrintCard() {
      const cardEl = this.$refs.businessCard || document.getElementById('staff-mobile-app-business-card')
      if (!cardEl) return

      const p = new Printd()
      const styles = `
        @page {
          size: 85mm 55mm;
          margin: 0;
        }
        @media print {
          html, body {
            margin: 0;
            padding: 0;
            width: 85mm;
            height: 55mm;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
        * {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }
        .business-card-preview {
          width: 85mm;
          height: 55mm;
          padding: 3mm 4.5mm 2.5mm 4.5mm;
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          background: #ffffff;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
          border: 1px solid #e2e8f0;
          position: relative;
          overflow: hidden;
        }
        .card-top-stripe {
          height: 1.2mm;
          width: 100%;
          background: linear-gradient(90deg, #E95594 0%, #F5D247 50%, #6EC4BB 100%) !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          position: absolute;
          top: 0;
          left: 0;
        }
        .card-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-bottom: 0.3mm solid #f1f5f9;
          padding-bottom: 1.5mm;
          padding-top: 0.8mm;
        }
        .card-logo {
          height: 5.5mm;
          max-width: 34mm;
          object-fit: contain;
        }
        .card-corp-badge {
          font-size: 4.8pt;
          font-weight: 800;
          letter-spacing: 0.5px;
          color: #E95594 !important;
          background: #fdf2f8 !important;
          border: 0.2mm solid #fbcfe8 !important;
          padding: 0.5mm 1.8mm;
          border-radius: 0.8mm;
          text-transform: uppercase;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        .card-body {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex: 1;
          padding: 1.6mm 0;
        }
        .card-user-info {
          display: flex;
          flex-direction: column;
          justify-content: center;
          padding-right: 2.5mm;
          flex: 1;
          min-width: 0;
        }
        .card-user-profile-wrap {
          display: flex;
          align-items: center;
          margin-bottom: 1mm;
        }
        .card-user-avatar {
          width: 9.5mm;
          height: 9.5mm;
          border-radius: 50%;
          object-fit: cover;
          margin-right: 2mm;
          border: 0.3mm solid #cbd5e1;
          flex-shrink: 0;
        }
        .card-user-avatar-placeholder {
          width: 9.5mm;
          height: 9.5mm;
          border-radius: 50%;
          background: #E95594 !important;
          color: #ffffff !important;
          font-weight: 800;
          font-size: 4pt;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 2mm;
          flex-shrink: 0;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        .card-user-text {
          min-width: 0;
        }
        .card-user-name {
          font-size: 10pt;
          font-weight: 700;
          color: #1f1c24;
          line-height: 1.2;
          margin-bottom: 0.3mm;
        }
        .card-user-role {
          font-size: 6pt;
          font-weight: 800;
          color: #E95594 !important;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 0.3mm;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        .card-user-email {
          font-size: 6pt;
          color: #475569;
          word-break: break-all;
          display: flex;
          align-items: center;
        }
        .card-user-email .email-icon {
          width: 2.2mm;
          height: 2.2mm;
          margin-right: 1mm;
          flex-shrink: 0;
        }
        .card-qr-column {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .card-qr-box {
          background: #ffffff;
          padding: 1mm;
          border: 0.3mm solid #cbd5e1;
          border-radius: 1.5mm;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .card-qr-box svg {
          width: 20mm !important;
          height: 20mm !important;
          display: block;
        }
        .card-qr-label {
          font-size: 4.8pt;
          font-weight: 700;
          color: #475569;
          letter-spacing: 0.6px;
          text-transform: uppercase;
          margin-top: 1.2mm;
          text-align: center;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        .card-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-top: 0.3mm solid #f1f5f9;
          padding-top: 1mm;
        }
        .card-footer-text {
          font-size: 4.5pt;
          font-weight: 700;
          color: #94a3b8;
          letter-spacing: 0.5px;
          text-transform: uppercase;
        }
        .brand-dots {
          display: inline-flex;
          align-items: center;
        }
        .brand-dots span {
          width: 1.6mm;
          height: 1.6mm;
          border-radius: 50%;
          display: inline-block;
          margin-left: 1mm;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        .brand-dot-pink { background: #E95594 !important; }
        .brand-dot-yellow { background: #F5D247 !important; }
        .brand-dot-teal { background: #6EC4BB !important; }
      `
      p.print(cardEl, [styles], [], ({ launchPrint }) => {
        setTimeout(() => {
          launchPrint()
        }, 150)
      })
    }
  }
}
</script>

<style lang="scss">
.staff-mobile-security-popup {
  .vs-popup {
    width: 440px !important;
    max-width: 95vw !important;
  }
}

.bg-light-primary {
  background-color: rgba(var(--vs-primary), 0.05);
}

.bg-light-success {
  background-color: rgba(var(--vs-success), 0.08);
}

.bg-light-warning {
  background-color: rgba(var(--vs-warning), 0.08);
}

/* Business Card Preview Styles */
.business-card-preview {
  width: 360px;
  min-height: 226px;
  padding: 16px 18px 12px 18px;
  background: linear-gradient(135deg, #ffffff 0%, #fafbfc 100%);
  color: #1f1c24;
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 12px 28px -6px rgba(0, 0, 0, 0.09), 0 4px 10px -2px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-sizing: border-box;
  position: relative;
  overflow: hidden;

  .card-top-stripe {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #E95594 0%, #F5D247 50%, #6EC4BB 100%);
  }

  .card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid #f1f5f9;
    padding-bottom: 10px;
    padding-top: 4px;
  }

  .card-logo {
    height: 26px;
    max-width: 135px;
    object-fit: contain;
  }

  .card-corp-badge {
    font-size: 8px;
    font-weight: 800;
    letter-spacing: 0.9px;
    color: #E95594;
    background: #fdf2f8;
    border: 1px solid #fbcfe8;
    padding: 3px 8px;
    border-radius: 4px;
    text-transform: uppercase;
  }

  .card-body {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 0;
  }

  .card-user-info {
    flex: 1;
    padding-right: 14px;
    min-width: 0;
  }

  .card-user-profile-wrap {
    display: flex;
    align-items: center;
    margin-bottom: 6px;
  }

  .card-user-avatar {
    width: 42px;
    height: 42px;
    border-radius: 50%;
    object-fit: cover;
    margin-right: 10px;
    border: 2px solid #ffffff;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
    flex-shrink: 0;
  }

  .card-user-avatar-placeholder {
    width: 42px;
    height: 42px;
    border-radius: 50%;
    background: linear-gradient(135deg, #E95594 0%, #D83B7E 100%);
    color: #ffffff;
    font-weight: 800;
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 10px;
    box-shadow: 0 2px 6px rgba(233, 85, 148, 0.25);
    flex-shrink: 0;
  }

  .card-user-text {
    min-width: 0;
  }

  .card-user-name {
    font-size: 15px;
    font-weight: 700;
    color: #1f1c24;
    line-height: 1.25;
    margin-bottom: 2px;
  }

  .card-user-role {
    font-size: 10px;
    font-weight: 800;
    color: #E95594;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    margin-bottom: 2px;
  }

  .card-user-email {
    font-size: 11px;
    color: #64748b;
    word-break: break-all;
    display: flex;
    align-items: center;

    .email-icon {
      margin-right: 5px;
      color: #94a3b8;
      flex-shrink: 0;
    }
  }

  .card-qr-column {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .card-qr-box {
    background: #ffffff;
    padding: 5px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.03);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .card-qr-label {
    font-size: 8px;
    font-weight: 700;
    color: #64748b;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    margin-top: 6px;
    text-align: center;
  }

  .card-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-top: 1px solid #f1f5f9;
    padding-top: 8px;
    margin-top: 4px;
  }

  .card-footer-text {
    font-size: 8px;
    font-weight: 700;
    letter-spacing: 0.7px;
    color: #94a3b8;
    text-transform: uppercase;
  }

  .brand-dots {
    display: inline-flex;
    align-items: center;
    gap: 5px;

    span {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      display: inline-block;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
    }
    .brand-dot-pink { background: #E95594; }
    .brand-dot-yellow { background: #F5D247; }
    .brand-dot-teal { background: #6EC4BB; }
  }
}
</style>
